import { useEffect, useRef, useState, useCallback } from "react";
import { fetchAuthSession } from "aws-amplify/auth";

const WEBSOCKET_URL = process.env.REACT_APP_WEBSOCKET_URL || "";

export interface WebSocketMessage {
  type: string;
  addressId?: string;
  timestamp?: string;
  [key: string]: any;
}

export interface UseWebSocketOptions {
  onMessage?: (message: WebSocketMessage) => void;
  enabled?: boolean;
}

export function useWebSocket(options: UseWebSocketOptions = {}) {
  const { onMessage, enabled = true } = options;
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const onMessageRef = useRef(onMessage);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000; // 3 seconds

  // Update the ref when onMessage changes, but don't trigger reconnection
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const connect = useCallback(async () => {
    if (!enabled) {
      console.log("WebSocket disabled");
      return;
    }
    
    if (!WEBSOCKET_URL) {
      console.error("WebSocket URL not configured. Set REACT_APP_WEBSOCKET_URL environment variable.");
      setError("WebSocket URL not configured");
      return;
    }

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
    }

    try {
      // Get auth token
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        console.error("No auth token available for WebSocket connection");
        setError("Authentication required");
        return;
      }

      // Connect to WebSocket with token as query parameter
      const wsUrl = `${WEBSOCKET_URL}?token=${encodeURIComponent(token)}`;
      console.log("Attempting to connect to WebSocket:", wsUrl.replace(token, "[TOKEN]"));
      console.log("Token present:", !!token, "Token length:", token?.length || 0);
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log("WebSocket connected");
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          console.log("WebSocket message received:", message);
          if (onMessageRef.current) {
            onMessageRef.current(message);
          }
        } catch (err) {
          console.error("Error parsing WebSocket message:", err);
        }
      };

      ws.onerror = (event) => {
        console.error("WebSocket error:", event);
        setError("WebSocket connection error");
      };

      ws.onclose = (event) => {
        console.log("WebSocket closed:", event.code, event.reason);
        setIsConnected(false);
        wsRef.current = null;

        // Set error message based on close code
        if (event.code === 1006) {
          setError("Connection closed abnormally. Check if WebSocket URL is correct and backend is deployed.");
        } else if (event.code === 1002) {
          setError("Protocol error. Check WebSocket URL format (should start with wss://).");
        } else if (event.code === 1003) {
          setError("Invalid data received.");
        } else if (event.code !== 1000) {
          setError(`Connection closed with code ${event.code}: ${event.reason || "Unknown reason"}`);
        }

        // Attempt to reconnect if not a normal closure
        if (event.code !== 1000 && enabled) {
          if (reconnectAttemptsRef.current < maxReconnectAttempts) {
            reconnectAttemptsRef.current += 1;
            console.log(
              `Attempting to reconnect (${reconnectAttemptsRef.current}/${maxReconnectAttempts})...`
            );
            reconnectTimeoutRef.current = setTimeout(() => {
              connect();
            }, reconnectDelay);
          } else {
            setError(`Failed to reconnect after ${maxReconnectAttempts} attempts.`);
          }
        }
      };

      wsRef.current = ws;
    } catch (err: any) {
      console.error("Error connecting to WebSocket:", err);
      setError(err.message || "Failed to connect to WebSocket");
    }
  }, [enabled]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, "Client disconnecting");
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  useEffect(() => {
    if (enabled) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  return {
    isConnected,
    error,
    connect,
    disconnect,
  };
}

